import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency, LOGO_FALLBACK_URL } from './defaultData';
import {
  NAVY, GOLD, LIGHT_GRAY, TEXT_COLOR, MID_GRAY, WHITE, PAGE, CONTENT_WIDTH,
  loadImageAsBase64, ensureSpace, addSimpleFooters, savePDF,
  renderNavyHeader,
} from './pdfShared';

// CONTENT_W alias for readability in this module
const CONTENT_W = CONTENT_WIDTH;

// ── Utilities: imported from pdfShared.js ─────────────────────────────────────
// loadImageAsBase64, ensureSpace — all in pdfShared

// ── Header: Navy banner with logo + company info + INVOICE title ──────────────
async function renderHeader(doc, invoiceData) {
  const ci = invoiceData.company_info || {};
  const HEADER_H = 42;
  const yAfterHeader = await renderNavyHeader(doc, {
    logoUrl: ci.logo_url || LOGO_FALLBACK_URL,
    companyName: ci.name,
    acn: null,        // handled by rightSideContent callback — avoids double-render
    address: ci.address,
    phone: ci.phone,
    director: null, // invoice header doesn't show director
    LOGO_FALLBACK_URL,
    rightSideContent: (doc, rightX, textX, logoW) => {
      // Override right side: show ACN/MOB/Address on left side, INVOICE label on right
      const leftTextX = textX;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(200, 200, 200);
      if (ci.acn) doc.text(`ACN ${ci.acn}`, leftTextX, 19);
      if (ci.phone) doc.text(`MOB: ${ci.phone}`, leftTextX, 24);
      if (ci.address) doc.text(ci.address, leftTextX, 29);

      // "INVOICE" label on the right
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.setTextColor(...GOLD);
      doc.text('INVOICE', rightX, 22, { align: 'right' });

      // Invoice number beneath
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(200, 200, 200);
      doc.text(invoiceData.invoice_no || '', rightX, 31, { align: 'right' });
    },
  });
  // renderNavyHeader uses doc.setDrawColor for gold line at HEADER_H;
  // invoice also has a filled gold rect at that position
  // Override: add a filled gold accent (invoice-specific)
  doc.setFillColor(...GOLD);
  doc.rect(0, HEADER_H, PAGE.width, 1.2, 'F');
  return HEADER_H + 1.2 + 7;
}

// ── Invoice meta + Bill To (two columns) ─────────────────────────────────────
function renderMetaSection(doc, invoiceData, y) {
  const colW = CONTENT_W / 2 - 4;
  const leftX = PAGE.margin;
  const rightX = PAGE.margin + CONTENT_W / 2 + 4;

  // Section box background
  doc.setFillColor(...LIGHT_GRAY);
  doc.roundedRect(PAGE.margin, y, CONTENT_W, 32, 2, 2, 'F');

  // ─ Left: invoice details ─
  const labelX = leftX + 3;
  const valueX = leftX + 32;
  let ly = y + 7;
  const lineH = 5.5;

  const rows = [
    ['Invoice No:',  invoiceData.invoice_no || '—'],
    ['Date:',        invoiceData.invoice_date || '—'],
    ['Due Date:',    invoiceData.due_date || '—'],
    ['Quote Ref:',   invoiceData.quote_no || '—'],
  ];

  for (const [lbl, val] of rows) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...MID_GRAY);
    doc.text(lbl, labelX, ly);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT_COLOR);
    doc.text(val, valueX, ly);
    ly += lineH;
  }

  // ─ Right: Bill To ─
  let ry = y + 7;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...NAVY);
  doc.text('BILL TO', rightX, ry);
  ry += 5;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...TEXT_COLOR);
  doc.text(invoiceData.client_name || '—', rightX, ry);
  ry += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...MID_GRAY);
  if (invoiceData.contact_person) { doc.text(`Attn: ${invoiceData.contact_person}`, rightX, ry); ry += 4.5; }
  if (invoiceData.client_abn) { doc.text(`ABN: ${invoiceData.client_abn}`, rightX, ry); ry += 4.5; }
  if (invoiceData.client_address) {
    const lines = doc.splitTextToSize(invoiceData.client_address, colW);
    for (const line of lines) { doc.text(line, rightX, ry); ry += 4.5; }
  }

  y += 32 + 6;

  // Project name + address
  if (invoiceData.project_name) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...MID_GRAY);
    doc.text('Project:', PAGE.margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT_COLOR);
    doc.text(invoiceData.project_name, PAGE.margin + 20, y);
    y += 5;
  }
  if (invoiceData.project_address) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...MID_GRAY);
    doc.text('Site:', PAGE.margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT_COLOR);
    const addrLines = doc.splitTextToSize(invoiceData.project_address, CONTENT_W - 22);
    for (const line of addrLines) { doc.text(line, PAGE.margin + 20, y); y += 4.5; }
    y += 1;
  }

  return y + 2;
}

// ── Line items table ──────────────────────────────────────────────────────────
function renderLineItems(doc, invoiceData, subtotal, gstEnabled, gstAmount, total, y) {
  const items = (invoiceData.items || []).filter(it => it.description || it.amount);

  const body = items.map(it => [
    it.description || '',
    formatCurrency(Number(it.amount) || 0),
  ]);

  // Totals footer rows
  const footRows = [];
  footRows.push([{ content: 'Subtotal', styles: { halign: 'right', fontStyle: 'bold', textColor: [...MID_GRAY] } }, formatCurrency(subtotal)]);
  if (gstEnabled) {
    footRows.push([{ content: 'GST (10%)', styles: { halign: 'right', textColor: [...MID_GRAY] } }, formatCurrency(gstAmount)]);
  }
  footRows.push([
    { content: 'TOTAL', styles: { halign: 'right', fontStyle: 'bold', fontSize: 10, textColor: [...NAVY] } },
    { content: formatCurrency(total), styles: { fontStyle: 'bold', fontSize: 10, textColor: [...GOLD] } },
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Description', 'Amount']],
    body,
    foot: footRows,
    theme: 'striped',
    margin: { left: PAGE.margin, right: PAGE.margin },
    tableWidth: CONTENT_W,
    headStyles: {
      fillColor: NAVY,
      textColor: WHITE,
      fontSize: 8.5,
      fontStyle: 'bold',
      cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
    },
    bodyStyles: {
      fontSize: 8.5,
      textColor: TEXT_COLOR,
      cellPadding: { top: 3.5, bottom: 3.5, left: 4, right: 4 },
    },
    alternateRowStyles: { fillColor: LIGHT_GRAY },
    footStyles: {
      fillColor: WHITE,
      textColor: TEXT_COLOR,
      fontSize: 8.5,
      fontStyle: 'normal',
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
    },
    columnStyles: {
      0: { cellWidth: CONTENT_W - 45 },
      1: { cellWidth: 45, halign: 'right' },
    },
    showFoot: 'lastPage',
  });

  return doc.lastAutoTable.finalY + 6;
}

// ── Payment details ───────────────────────────────────────────────────────────
function renderPaymentDetails(doc, invoiceData, y) {
  y = ensureSpace(doc, y, 40);

  doc.setFillColor(...LIGHT_GRAY);
  doc.roundedRect(PAGE.margin, y, CONTENT_W, 30, 2, 2, 'F');

  doc.setFillColor(...GOLD);
  doc.rect(PAGE.margin, y, 3, 30, 'F');

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text('PAYMENT DETAILS', PAGE.margin + 7, y + 7);

  const pd = invoiceData.payment_details || {};
  const details = [
    ['Bank',       pd.bank_name       || '—'],
    ['Account',    pd.account_name    || '—'],
    ['BSB',        pd.bsb             || '—'],
    ['Account No', pd.account_number  || '—'],
    ['Reference',  invoiceData.invoice_no || ''],
  ];

  const startX = PAGE.margin + 7;
  let dy = y + 13;
  for (const [lbl, val] of details) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...MID_GRAY);
    doc.text(`${lbl}:`, startX, dy);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT_COLOR);
    doc.text(val, startX + 26, dy);
    dy += 4.2;
  }

  return y + 30 + 6;
}

// ── Notes ─────────────────────────────────────────────────────────────────────
function renderNotes(doc, notes, y) {
  if (!notes || !notes.trim()) return y;
  y = ensureSpace(doc, y, 20);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...NAVY);
  doc.text('NOTES', PAGE.margin, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...MID_GRAY);
  const lines = doc.splitTextToSize(notes, CONTENT_W);
  for (const line of lines) {
    y = ensureSpace(doc, y, 5);
    doc.text(line, PAGE.margin, y);
    y += 4.5;
  }

  return y + 4;
}

// ── Status stamp ──────────────────────────────────────────────────────────────
function renderStatusStamp(doc, status) {
  if (!status || status === 'draft') return;
  const label = status.toUpperCase();
  const color = status === 'paid' ? [22, 163, 74] : [37, 99, 235];

  doc.saveGraphicsState();
  doc.setGState(doc.GState({ opacity: 0.12 }));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(52);
  doc.setTextColor(...color);

  const cx = PAGE.width / 2;
  const cy = PAGE.height / 2;
  doc.text(label, cx, cy, { align: 'center', angle: 35 });
  doc.restoreGraphicsState();
}

// addFooters replaced by addSimpleFooters from pdfShared.js
// savePDF imported from pdfShared.js

// ── Main export ───────────────────────────────────────────────────────────────
export async function generateInvoicePDF(invoiceData, subtotal, gstAmount, total, mode = 'download') {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  doc.setTextColor(...TEXT_COLOR);

  // 1. Header
  let y = await renderHeader(doc, invoiceData);

  // 2. Status watermark (PAID / SENT)
  if (invoiceData.status && invoiceData.status !== 'draft') {
    renderStatusStamp(doc, invoiceData.status);
  }

  // 3. Meta + Bill To
  y = renderMetaSection(doc, invoiceData, y);

  // 4. Line items + totals
  y = renderLineItems(doc, invoiceData, subtotal, invoiceData.gst_enabled, gstAmount, total, y);

  // 5. Payment details
  y = renderPaymentDetails(doc, invoiceData, y);

  // 6. Notes
  y = renderNotes(doc, invoiceData.notes, y);

  // 7. Footer page numbers
  addSimpleFooters(doc, invoiceData.invoice_no);

  // 8. Save / Preview
  const filename = `${invoiceData.invoice_no || 'Invoice'}_${invoiceData.client_name || 'Client'}.pdf`
    .replace(/[/\\?%*:|"<>]/g, '-');
  if (mode === 'preview') {
    const blob = doc.output('blob');
    window.open(URL.createObjectURL(blob), '_blank');
  } else {
    await savePDF(doc, filename);
  }
}

export function computeInvoiceFinancials(invoiceData) {
  const subtotal = invoiceData?.items?.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) || 0;
  const gstAmount = invoiceData?.gst_enabled ? subtotal * 0.1 : 0;
  const total = subtotal + gstAmount;
  return { subtotal, gstAmount, total };
}
